import { apiClient } from '@/utils/api-client'

/**
 * Percentage rollout service for managing rollout configurations on targets.
 *
 * Provides methods to create, update, remove, and complete percentage-based
 * rollouts for feature flag targets.
 */
class PercentageRolloutService {
    /**
     * Create a new percentage rollout on a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @param {Object} payload - Rollout configuration
     * @returns {Promise<Object>} Created rollout data
     * @throws {string} Error message if creation fails
     */
    async create(projectKey, environmentKey, targetUlid, payload) {
        return await apiClient
            .post(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '/rollout',
                payload
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Update an existing percentage rollout on a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @param {Object} payload - Updated rollout configuration
     * @returns {Promise<Object>} Updated rollout data
     * @throws {string} Error message if update fails
     */
    async update(projectKey, environmentKey, targetUlid, payload) {
        return await apiClient
            .put(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '/rollout',
                payload
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Remove a percentage rollout from a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @returns {Promise<Object>} Response data
     * @throws {string} Error message if removal fails
     */
    async remove(projectKey, environmentKey, targetUlid) {
        return await apiClient
            .delete(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '/rollout'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Complete a percentage rollout on a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @returns {Promise<Object>} Response data
     * @throws {string} Error message if completion fails
     */
    async complete(projectKey, environmentKey, targetUlid) {
        return await apiClient
            .post(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '/rollout/complete',
                {}
            )
            .then((response) => {
                return response.data
            })
    }
}

export default new PercentageRolloutService()
